import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, AlertTriangle, RotateCcw } from "lucide-react";
import { useForceDark } from "@/hooks/useForceDark";

/**
 * Landing page for the "Cancel deletion" link in the confirmation email.
 * Un-authenticated — validates the token via the backend and shows the
 * outcome. We deliberately require a click before firing so bot preview
 * fetchers can't auto-cancel by scanning the URL.
 */
export default function DeletionCancel() {
  useForceDark();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("idle"); // idle | busy | ok | error
  const [errorMsg, setErrorMsg] = useState("");
  const [email, setEmail] = useState("");

  const confirm = async () => {
    setState("busy");
    try {
      const { data } = await api.post("/deletion-request/cancel-with-token", { token });
      setEmail(data?.email || "");
      setState("ok");
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || "Could not cancel — the link may already have been used.");
      setState("error");
    }
  };

  useEffect(() => {
    if (!token) { setState("error"); setErrorMsg("Missing token in the link."); }
  }, [token]);

  return (
    <div className="min-h-screen bg-[#0A0F1C] text-white grid place-items-center px-6 py-10 grain">
      <div className="w-full max-w-md relative z-10">
        <Link to="/" className="flex items-center gap-3 mb-8 hover:opacity-80">
          <img src="/logo.png" alt="Elsenham YFC" className="w-11 h-11 rounded-full object-contain" />
          <div className="font-display font-black text-lg leading-none tracking-tight">ELSENHAM YFC</div>
        </Link>

        <div className="card-tactical rounded-md p-6" data-testid="deletion-cancel-card">
          {state === "idle" && (
            <>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded bg-[#83B68B]/15 border border-[#83B68B]/30 flex items-center justify-center">
                  <RotateCcw className="w-5 h-5 text-[#83B68B]" />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Undo deletion</div>
                  <div className="font-display font-black text-2xl leading-tight mt-1">Keep my account</div>
                </div>
              </div>
              <p className="text-sm text-white/70 leading-relaxed">
                You&#39;re about to cancel your scheduled account deletion.
                Tap the button below to confirm and we&#39;ll keep your
                account exactly as it was — no data will be scrubbed.
              </p>
              <button
                type="button"
                onClick={confirm}
                data-testid="deletion-cancel-confirm-btn"
                className="btn-volt w-full mt-5 py-3 rounded-md inline-flex items-center justify-center gap-2"
              >
                <RotateCcw className="w-4 h-4" />
                Confirm — keep my account
              </button>
              <div className="text-[11px] opacity-50 mt-3">
                This link is single-use. If you&#39;ve already cancelled or the deletion has already run, we&#39;ll let you know below.
              </div>
            </>
          )}
          {state === "busy" && (
            <div className="text-center py-6 font-display tracking-widest text-white/60">Cancelling…</div>
          )}
          {state === "ok" && (
            <div data-testid="deletion-cancel-ok">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded bg-[#83B68B]/15 border border-[#83B68B]/30 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-[#83B68B]" />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">All good</div>
                  <div className="font-display font-black text-2xl leading-tight mt-1">Deletion cancelled</div>
                </div>
              </div>
              <p className="text-sm text-white/70 leading-relaxed">
                We&#39;ve kept your account exactly as it was.
                {email ? <> A confirmation is on its way to <strong className="text-white/90">{email}</strong>.</> : null}
              </p>
              <Link to="/login" className="btn-volt inline-flex mt-5 px-6 py-3 rounded-md text-sm items-center gap-2">
                Back to sign in
              </Link>
            </div>
          )}
          {state === "error" && (
            <div data-testid="deletion-cancel-error">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded bg-red-500/15 border border-red-500/30 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-red-400">Nothing to cancel</div>
                  <div className="font-display font-black text-2xl leading-tight mt-1">Link expired</div>
                </div>
              </div>
              <p className="text-sm text-white/70 leading-relaxed">{errorMsg}</p>
              <Link to="/login" className="btn-volt inline-flex mt-5 px-6 py-3 rounded-md text-sm items-center gap-2">
                Back to sign in
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
